import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, fmtDate, slug } from "@/lib/api";
import { OutcomeBadge } from "@/components/RaceResult";
import { BreakdownTable } from "@/components/BreakdownTable";
import { SilksIcon } from "@/components/SilksIcon";
import { ShareRecordCard, tally } from "@/components/ShareRecordCard";
import { DateInput } from "@/components/DateInput";
import { TipPerformance } from "@/components/TipPerformance";

const NONE = { state: "", track: "", date_from: "", date_to: "" };
const field = "bg-[#131C31] border border-[#202E4C] rounded-full px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500";

const Filters = ({ f, setF, options }) => {
  const tracks = (options?.tracks || []).filter((t) => !f.state || t.state === f.state);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v, ...(k === "state" ? { track: "" } : {}) }));
  const active = Object.values(f).some(Boolean);
  return (
    <div className="surface p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="record-filters">
      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-slate-500">State
        <select data-testid="record-filter-state" value={f.state} onChange={(e) => set("state")(e.target.value)} className={field}>
          <option value="">All states</option>
          {(options?.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-slate-500 min-w-0">Track
        <select data-testid="record-filter-track" value={f.track} onChange={(e) => set("track")(e.target.value)} className={`${field} max-w-[14rem]`}>
          <option value="">All tracks</option>
          {tracks.map((t) => <option key={t.track} value={t.track}>{t.track}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-slate-500">From
        <DateInput testid="record-filter-from" value={f.date_from} onChange={set("date_from")} className={field} />
      </label>
      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-slate-500">To
        <DateInput testid="record-filter-to" value={f.date_to} onChange={set("date_to")} className={field} />
      </label>
      {active && <button data-testid="record-filter-clear" onClick={() => setF(NONE)} className="pill-btn pill-off">Clear filters</button>}
    </div>
  );
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="surface p-4 sm:p-5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="font-mono text-2xl sm:text-3xl font-extrabold mt-1 text-slate-50">{value == null ? "—" : `${value}%`}</p>
    <p className="text-xs text-slate-400 mt-1">{sub}</p>
  </div>
);

const groupByMeeting = (rows) => {
  const out = [];
  for (const r of rows) {
    const last = out[out.length - 1];
    if (last?.meeting_id === r.meeting_id) last.races.push(r);
    else out.push({ meeting_id: r.meeting_id, date: r.date, track: r.track, races: [r] });
  }
  return out;
};

const Placing = ({ p }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
    <span className={`w-5 h-5 rounded grid place-items-center font-mono font-bold text-[10px] ${p.position === 1 ? "bg-emerald-500 text-slate-950" : "bg-[#1B2640] border border-[#202E4C] text-slate-200"}`}>{p.position}</span>
    <span className="truncate max-w-[10rem]">{p.horse}</span>
  </span>
);

const RecordRace = ({ r }) => (
  <Link to={`/meeting/${r.meeting_id}?race=${r.race_number}`} data-testid={`record-row-${slug(r.track)}-${r.race_number}`}
    className="flex gap-3 sm:gap-4 px-4 py-3 hover:bg-[#1B2640]/50 transition-colors">
    <span className="font-mono font-extrabold text-blue-400 w-8 shrink-0 pt-0.5">R{r.race_number}</span>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Our selections</p>
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1.5">
        {r.selections.map((s, i) => (
          <span key={i} className="flex items-center gap-2 text-sm"><SilksIcon url={s.silk_url} colours={s.silk_colours} testid={`record-silk-${slug(r.track)}-${r.race_number}-${i + 1}`} /><span className="font-bold text-slate-100 truncate">{i + 1}. {s.horse}</span><OutcomeBadge outcome={s.outcome} /></span>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`record-result-${slug(r.track)}-${r.race_number}`}>
        <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Result</span>
        {r.placings.map((p) => <Placing key={`${p.position}-${p.number}`} p={p} />)}
      </div>
    </div>
  </Link>
);

const MeetingGroup = ({ g }) => {
  const { wins } = tally(g.races);
  return (
    <section className="surface overflow-hidden" data-testid={`record-meeting-${slug(g.track)}-${g.date}`}>
      <header className="flex items-baseline justify-between gap-3 px-4 py-3 border-b border-[#202E4C] bg-[#1B2640]/40">
        <h2 className="section-title min-w-0 truncate">{g.track} <span className="text-slate-400 font-normal text-sm">· {fmtDate(g.date)}</span></h2>
        <p className="text-xs text-slate-400 shrink-0" data-testid={`record-meeting-tally-${slug(g.track)}-${g.date}`}>{g.races.length} race{g.races.length > 1 ? "s" : ""} · top pick won {wins}</p>
      </header>
      <div className="divide-y divide-[#202E4C]">{g.races.map((r) => <RecordRace key={r.race_number} r={r} />)}</div>
      <div className="px-4 py-3 border-t border-[#202E4C]"><ShareRecordCard g={g} /></div>
    </section>
  );
};

export default function Record() {
  const [days, setDays] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState(NONE);
  useEffect(() => {
    let live = true;
    setErr("");
    const params = Object.fromEntries(Object.entries({ days, ...f }).filter(([, v]) => v));
    axios.get(`${API}/stats`, { params }).then((r) => live && setD(r.data)).catch(() => live && setErr("Couldn't load the record right now."));
    return () => { live = false; };
  }, [days, f]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow mb-2">The Weekend Edge</p>
      <h1 className="page-title mb-2">Results & Strike Rate</h1>
      <p className="text-slate-400 mb-6">Every published tip counts. Results are the official placings, entered after each race.</p>
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {[[null, "All time"], [30, "Last 30 days"], [7, "Last 7 days"]].map(([v, l]) => (
          <button key={l} data-testid={`record-period-${v || "all"}`} onClick={() => setDays(v)}
            className={`pill-btn ${days === v ? "pill-on" : "pill-off"}`}>{l}</button>
        ))}
      </div>
      <Filters f={f} setF={setF} options={d?.options} />
      {err && <p className="text-rose-300" data-testid="record-error">{err}</p>}
      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
            <Stat testid="stat-top-win" label="Top pick won" value={d.top_pick.win_pct} sub={`${d.top_pick.wins} of ${d.races_resulted} races`} />
            <Stat testid="stat-top-place" label="Top pick top 3" value={d.top_pick.place_pct} sub={`${d.top_pick.places} of ${d.races_resulted} races`} />
            <Stat testid="stat-any-win" label="A selection won" value={d.any_selection_won.pct} sub={`${d.any_selection_won.races} of ${d.races_resulted} races`} />
            <Stat testid="stat-all-place" label="All selections top 3" value={d.all_selections.place_pct} sub={`${d.all_selections.places} of ${d.all_selections.count} selections`} />
          </div>
          <p className="text-xs text-slate-500 mb-8" data-testid="record-counts">{d.races_tipped} race{d.races_tipped === 1 ? "" : "s"} tipped · {d.races_resulted} with results · {d.pending} awaiting results. "Top 3" means finished 1st, 2nd or 3rd. Selections scratched after publishing are left out. Past results don't guarantee future outcomes.</p>
          <TipPerformance t={d.tip_ledger} />
          <div className="grid lg:grid-cols-2 gap-4 mb-8">
            <BreakdownTable title="By state" rows={d.by_state} kind="state" />
            <BreakdownTable title="By track" rows={d.by_track} kind="track" />
          </div>
          {d.recent.length === 0 ? (
            <div data-testid="record-empty" className="rounded-xl border border-dashed border-[#202E4C] p-10 text-center text-slate-400">No results match these filters yet.</div>
          ) : (
            <div className="space-y-6" data-testid="record-list">
              {groupByMeeting(d.recent).map((g) => <MeetingGroup key={g.meeting_id} g={g} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
